import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { EAccountRole, ERoleAdminString } from 'src/app/shared/data/role.enum';
import { ToastService } from 'src/app/shared/services/toast.service';
import { IFilterPerfilAcesso, IPerfilAcesso } from '../../roles/models/role';
import { RolesService } from '../../roles/services/roles.service';
import { UsuarioAdminService } from '../services/usuario-admin.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { ISelect } from 'src/app/shared/data/select.interface';
import { RoleRegisterService } from 'src/app/shared/services/role-register.service';
import { ICompany } from '../../companies/models/company.interface';
import { PartnerService } from '../../partners/services/partner.service';
import { IPartner } from '../../partners/models/partner.interface';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { comPerfilLegado, perfisDoUsuario } from 'src/app/shared/utilities/perfis-do-usuario';
import { canPickCompany } from 'src/app/shared/utilities/company-scope';
import { CompanyService } from '../../companies/services/company.service';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';

@Component({
  selector: 'app-edit-usuario-admin',
  templateUrl: './edit-usuario-admin.component.html',
  styleUrls: ['./edit-usuario-admin.component.scss']
})
export class EditUsuarioAdminComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  usuarioAdminId?: string | null;

  formEditUsuarioAdmin = new UntypedFormGroup({
    id: new UntypedFormControl('', [Validators.required]),
    fullName: new UntypedFormControl('', [Validators.required]),
    document: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
    adminUserProfileIds: new UntypedFormControl([], [Validators.required]),
    accountRole: new UntypedFormControl(null),
    adminRole: new UntypedFormControl(null, [Validators.required]),
    companyId: new UntypedFormControl(null),
    partnerId: new UntypedFormControl(null)
  });

  opcoesPerfilAcesso: IPerfilAcesso[] = [];
  opcoesAdminRole: ISelect[] = [];
  opcoesAccountRole: ISelect[] = [];
  opcoesEmpresa: ICompany[] = [];
  opcoesPartner: IPartner[] = [];
  podeEscolherEmpresa = false;
  private empresasSub?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly usuariosAdminService: UsuarioAdminService,
    private readonly roleService: RolesService,
    private readonly toastService: ToastService,
    private readonly sessionService: SessionService,
    private readonly roleRegisterService: RoleRegisterService,
    private readonly partnerService: PartnerService,
    private readonly companyService: CompanyService
  ) {}

  public ngOnInit(): void {
    this.usuarioAdminId = this.activatedRoute.snapshot.paramMap.get('id');
    // Usuário de empresa não troca a empresa (fica fixa); interno e parceiro sem empresa escolhem.
    this.podeEscolherEmpresa = canPickCompany(this.sessionService.getSession());

    if (this.usuarioAdminId) {
      this.carregarDetalhesUsuarioAdmin(this.usuarioAdminId);
    }

    this.getListAdminRole();
    this.getListAccountRole();
    this.getListPartner();
  }

  ngOnDestroy(): void {
    this.empresasSub?.unsubscribe();
  }

  getListAdminRole(): void {
    this.roleRegisterService.getAdminRole().subscribe((response) => {
      this.opcoesAdminRole = response.data;
    });
  }

  getListAccountRole(): void {
    this.roleRegisterService.getAccountRole().subscribe((response) => {
      this.opcoesAccountRole = response.data;
    });
  }

  getListPartner(): void {
    this.partnerService.listarTodosParceirosParaSelect().subscribe((resposta) => {
      this.opcoesPartner = resposta.data;
    });
  }

  changeAccountRole(): void {
    this.formEditUsuarioAdmin.get('adminUserProfileIds')?.patchValue([]);
    this.getListPerfilAcesso();

    if (this.formEditUsuarioAdmin.get('accountRole')?.value === EAccountRole.Admin) {
      this.formEditUsuarioAdmin.get('partnerId')?.patchValue(null);
      this.formEditUsuarioAdmin.get('companyId')?.patchValue(null);
      this.empresasSub?.unsubscribe();
      this.opcoesEmpresa = [];
    }
  }

  changePartner(): void {
    this.formEditUsuarioAdmin.get('companyId')?.patchValue(null);
    this.getListEmpresas();
  }

  getListEmpresas(): void {
    if (!this.podeEscolherEmpresa) {
      return;
    }

    // O backend escopa pelo token (interno vê todas, parceiro só as dele); filtra pelo parceiro do usuário editado.
    const partnerId = this.formEditUsuarioAdmin.get('partnerId')?.value;

    // Cancela a busca anterior: a resposta atrasada aplicaria o filtro do parceiro antigo.
    this.empresasSub?.unsubscribe();
    this.empresasSub = this.companyService.listarEmpresasAtivas().subscribe({
      next: (resposta) => {
        this.opcoesEmpresa = resposta.data.filter((empresa) => !partnerId || empresa.partnerId === partnerId);
      },
      error: (erro) => {
        this.opcoesEmpresa = [];
        if (!interceptorAvisouErro(erro)) {
          this.toastService.toast('Erro ao carregar as empresas. Tente novamente.', EMessageType.Error);
        }
      }
    });
  }

  getListPerfilAcesso(): void {
    const filtros: IFilterPerfilAcesso = {
      pageNumber: 0,
      pageSize: 99999,
      active: true
    };

    const partnerId = this.sessionService.getSession().partnerId;
    const companyId = this.sessionService.getSession().companyId;
    const accountRole = this.formEditUsuarioAdmin.get('accountRole')?.value;

    if (partnerId) {
      filtros.partnerId = partnerId;
    }

    if (companyId) {
      filtros.companyId = companyId;
    }

    if (accountRole !== undefined) {
      filtros.accountRole = parseInt(accountRole);
    }

    this.roleService.carregarPerfil(filtros).subscribe((resposta) => {
      this.opcoesPerfilAcesso = resposta.data;
    });
  }

  public carregarDetalhesUsuarioAdmin(usuarioAdminId: string): void {
    this.formEditUsuarioAdmin.get('id')!.patchValue(usuarioAdminId);

    this.usuariosAdminService.detalheUsuarioAdmin(usuarioAdminId).subscribe((res) => {
      if (res.messages && res.messages.length > 0) {
        this.toastService.toast(res.messages);
      }
      this.formEditUsuarioAdmin.get('id')!.patchValue(res.data.id);
      this.formEditUsuarioAdmin.get('fullName')!.patchValue(res.data.fullName);
      this.formEditUsuarioAdmin.get('document')!.patchValue(res.data.document);
      this.formEditUsuarioAdmin.get('email')!.patchValue(res.data.email);
      this.formEditUsuarioAdmin.get('adminUserProfileIds')!.patchValue(perfisDoUsuario(res.data));
      this.formEditUsuarioAdmin.get('accountRole')!.patchValue(res.data.accountRole);
      this.formEditUsuarioAdmin.get('adminRole')!.patchValue(res.data.adminRole);
      this.formEditUsuarioAdmin.get('companyId')!.patchValue(res.data.companyId);
      this.formEditUsuarioAdmin.get('partnerId')!.patchValue(res.data.partnerId);

      this.getListPerfilAcesso();
      this.getListEmpresas();
    });
  }

  public editarUsuarioAdmin(): void {
    if (this.formEditUsuarioAdmin.invalid) {
      this.toastService.toast('Existem campos obrigatórios que não foram preenchidos.', EMessageType.Error);
      this.formEditUsuarioAdmin.markAllAsTouched();
      return;
    }

    const usuario = comPerfilLegado(this.formEditUsuarioAdmin.getRawValue());

    this.usuariosAdminService.editarUsuarioAdmin(usuario).subscribe((res) => {
      if (res.messages && res.messages.length > 0) {
        this.toastService.toast(res.messages);
      }

      // if (res.isSucceed) {
      this.router.navigateByUrl('/dash/usuarios-admin');
      // }
    });
  }
}
